import React, { useState } from "react";
import { CheckCircle2, Loader2, Mail, MapPin, Phone } from "lucide-react";
import { useSEO } from "@/lib/seo";
import { api, formatApiErrorDetail } from "@/lib/api";
import { StarMotif } from "@/components/common/StarMotif";

export default function Contact() {
    useSEO({ title: "Contact — RKS Systems", description: "Get in touch with the RKS Systems team." });
    const [form, setForm] = useState({ name: "", email: "", phone: "", company: "", message: "" });
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState("");
    const [success, setSuccess] = useState(false);

    const change = (k) => (e) => setForm({ ...form, [k]: e.target.value });
    const submit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        setError("");
        try {
            await api.post("/leads", { ...form, source: "contact_page" });
            setSuccess(true);
        } catch (e) {
            setError(formatApiErrorDetail(e?.response?.data?.detail) || "Something went wrong.");
        } finally {
            setSubmitting(false);
        }
    };
    return (
        <div data-testid="page-contact">
            <section className="bg-[hsl(222_47%_7%)] text-white noise">
                <div className="container-app py-20 lg:py-28">
                    <div className="max-w-3xl">
                        <span className="overline text-primary">Contact</span>
                        <h1 className="mt-4 font-heading text-5xl sm:text-6xl font-extrabold tracking-tight leading-[1.05]">
                            Say hello.
                        </h1>
                        <p className="mt-6 text-lg text-white/70 leading-relaxed">
                            Whether you&apos;re exploring, scoping, or ready to ship — we&apos;d love to hear from you.
                        </p>
                    </div>
                </div>
            </section>

            <section className="py-16 lg:py-24">
                <div className="container-app grid lg:grid-cols-[1.4fr,1fr] gap-10">
                    <div className="rounded-2xl border border-border bg-card p-8 lg:p-10">
                        {success ? (
                            <div className="text-center py-10" data-testid="contact-success">
                                <div className="inline-flex h-16 w-16 items-center justify-center rounded-full bg-primary/10 text-primary">
                                    <CheckCircle2 className="h-8 w-8" strokeWidth={1.75} />
                                </div>
                                <h2 className="mt-6 font-heading text-2xl font-bold">Message sent.</h2>
                                <p className="mt-3 text-muted-foreground">We&apos;ll get back to you within one business day.</p>
                            </div>
                        ) : (
                            <form onSubmit={submit} className="space-y-5" data-testid="contact-form">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                                    <div>
                                        <label className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">Name *</label>
                                        <input required data-testid="contact-name" value={form.name} onChange={change("name")} className="mt-1.5 w-full rounded-md border border-input bg-white px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary" />
                                    </div>
                                    <div>
                                        <label className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">Email *</label>
                                        <input required type="email" data-testid="contact-email" value={form.email} onChange={change("email")} className="mt-1.5 w-full rounded-md border border-input bg-white px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary" />
                                    </div>
                                    <div>
                                        <label className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">Company</label>
                                        <input data-testid="contact-company" value={form.company} onChange={change("company")} className="mt-1.5 w-full rounded-md border border-input bg-white px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary" />
                                    </div>
                                    <div>
                                        <label className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">Phone</label>
                                        <input data-testid="contact-phone" value={form.phone} onChange={change("phone")} className="mt-1.5 w-full rounded-md border border-input bg-white px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary" />
                                    </div>
                                </div>
                                <div>
                                    <label className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">Message *</label>
                                    <textarea required rows="6" data-testid="contact-message" value={form.message} onChange={change("message")} className="mt-1.5 w-full rounded-md border border-input bg-white px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary" />
                                </div>
                                {error && <div className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>}
                                <button type="submit" disabled={submitting} data-testid="contact-submit" className="inline-flex items-center justify-center gap-2 rounded-md bg-primary px-6 py-3 font-semibold text-white btn-lift hover:bg-primary/90 disabled:opacity-50">
                                    {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                                    {submitting ? "Sending..." : "Send message"}
                                </button>
                            </form>
                        )}
                    </div>

                    <aside className="rounded-2xl border border-border bg-secondary/40 p-8 space-y-6">
                        <div>
                            <StarMotif className="h-8 w-8 text-primary" label="RKS" labelColor="#ffffff" />
                            <h3 className="mt-4 font-heading text-lg font-bold">Reach us directly</h3>
                        </div>
                        <div className="space-y-4 text-sm">
                            <div className="flex items-start gap-3">
                                <Mail className="h-4 w-4 mt-0.5 text-primary" strokeWidth={1.75} />
                                <div>
                                    <div className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">Sales</div>
                                    <a href="mailto:info@rkssystems.com" className="text-foreground hover:text-primary">info@rkssystems.com</a>
                                </div>
                            </div>
                            <div className="flex items-start gap-3">
                                <Phone className="h-4 w-4 mt-0.5 text-primary" strokeWidth={1.75} />
                                <div>
                                    <div className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">Phone</div>
                                    <a href="tel:+918309575095" className="text-foreground hover:text-primary">+91 83095 75095</a>
                                </div>
                            </div>
                            <div className="flex items-start gap-3">
                                <MapPin className="h-4 w-4 mt-0.5 text-primary" strokeWidth={1.75} />
                                <div>
                                    <div className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">Delivery HQ</div>
                                    <div>Building 9, Mindspace, Madhapur, Hyderabad, 500081</div>
                                    <div className="text-muted-foreground text-xs mt-1">Serving US &amp; Global Enterprises</div>
                                </div>
                            </div>
                        </div>
                    </aside>
                </div>
            </section>
        </div>
    );
}
